<template>
  <PaymentsTable
    :items="list"
    :title="title"
    @refresh="refresh"
    :class="{ 'table-loading': loadingList }"
    :listOptions="listOptions"
    @orderUpdate="orderUpdate($event)"
    @filterUpdate="filterUpdate($event)"
    :inertProp="anyMenusOpen || inertProp"
    @menuEvent="menus.table = $event"
  >
    <template #actions>
      <TableOptionsResetBtn
        :enabled="anyActiveListOptions"
        @input="resetListOptions"
        :inert="anyMenusOpen || inertProp"
      />
      <div class="payments-table__action">
        <div class="payments-table__action__label">Filter by:</div>
        <v-overlay
          :value="menus.filters"
          @click.native.stop="closeFilters()"
        />
        <v-menu
          bottom
          offset-y
          :close-on-click="false"
          :close-on-content-click="false"
          v-model="menus.filters"
          light
          class="filters-menu"
          content-class="filters-menu__content"
        >
          <template v-slot:activator="{ on }">
            <TableActiveFilters
              v-on="on"
              @openMenu="menus.filters = !menus.filters"
              :listOptions="listOptions"
              :fields="filterFields"
              @resetFilter="resetFilter($event)"
              :activeListOption="activeListOption"
              ref="tableActiveFilters"
              :filtersMenu="menus.filters"
              :inert="inertProp && !menus.filters"
            />
          </template>
          <v-card
            class="filters-menu__content__card"
            :aria-modal="menus.filters"
            :aria-live="menus.filters ? 'polite' : false"
          >
            <div class="filters-menu__body">
              <v-expansion-panels
                v-model="filterPanels"
                multiple
                flat
              >
                <v-expansion-panel>
                  <v-expansion-panel-header> Creator </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <UsersSelector
                      v-model="listOptions.user_id"
                      placeholder="All"
                      @input="
                        optionsUpdate();
                      closeFilters();
                      "
                      includeBlocked
                      multiple
                      itemTooltip="email"
                    />
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel>
                  <v-expansion-panel-header> Project </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <InternalProjectsSelector
                      v-model="listOptions.internal_project_id"
                      emptyLabel="All"
                      @input="
                        optionsUpdate();
                      closeFilters();
                      "
                      multiple
                      chipText="code"
                      @remove="
                        removeFromFilter('internal_project_id', $event);
                      optionsUpdate();
                      closeFilters();
                      "
                    />
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <TableDateFilterPanel
                  :listOptions="listOptions"
                  fieldName="created_at"
                  @filterUpdate="filterMenuUpdate($event)"
                  fieldLabel="Date Created"
                />
                <TableOptionsFilterPanel
                  :listOptions="listOptions"
                  fieldName="status"
                  @filterUpdate="filterMenuUpdate($event)"
                  fieldLabel="Status"
                  selectAll
                  reset
                  :defaultListOptions="getDefaultListOptions()"
                />
                <v-expansion-panel>
                  <v-expansion-panel-header> Role </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <SkillsSelector
                      v-model="listOptions.skill_id"
                      placeholder="Role"
                      :rules="[validation.required]"
                      @input="
                        optionsUpdate();
                      closeFilters();
                      "
                    />
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </v-card>
        </v-menu>
      </div>
      <div
        class="payments-table__action"
        :inert="inertProp || anyMenusOpen"
      >
        <div class="payments-table__action__label"></div>
        <TextField
          v-model="searchInputBuffer"
          placeholder="Search"
          inputOnEnter
          @enter="searchInputEvent"
          ref="searchField"
        />
      </div>
      <div
        class="payments-table__secondary-actions"
        :inert="inertProp || anyMenusOpen"
      >
        <div class="
            payments-table__action
            payments-table__action--compact
            payments-table__action--download
          ">
          <ActionsMenu
            @close="menus.download = false"
            @toggle="menus.download = !menus.download"
            :menu="menus.download"
            themeDisabledPage
            icon="$download"
            :iconSize="24"
            :inertProp="inertProp || anyMenusOpen"
          >
            <form
              method="post"
              :action="`${apiDomain}/api/v1/profile_side/invoices/csv`"
              target="_blank"
              style="margin-bottom: 21px"
            >
              <input
                type="hidden"
                name="token"
                :value="$store.getters['auth/token']"
              />
              <input
                type="hidden"
                v-for="item in Object.entries(listParams)"
                :key="`listParams-${item[0]}`"
                :name="item[0]"
                :value="item[1]"
              />
              <v-btn
                type="submit"
                color="#56565A"
                elevation="0"
                style="color: white !important"
              >Export All Payments</v-btn>
            </form>
            <form
              method="post"
              :action="`${apiDomain}/api/v1/profile_side/invoices/csv_itemized`"
              target="_blank"
              class="form-button"
            >
              <input
                type="hidden"
                name="token"
                :value="$store.getters['auth/token']"
              />
              <input
                type="hidden"
                v-for="item in Object.entries(listParams)"
                :key="`listParams-${item[0]}`"
                :name="item[0]"
                :value="item[1]"
              />
              <v-btn
                type="submit"
                color="#56565A"
                elevation="0"
                style="color: white !important"
              >Itemized Export</v-btn>
            </form>
          </ActionsMenu>
        </div>
        <TableOptionsResetBtn
          :enabled="anyActiveListOptions"
          @input="resetListOptions"
          mobile
        />
      </div>
    </template>
    <template #pagination>
      <TablePagination
        v-if="paginationEnabled"
        :page="page"
        :totalPages="totalPages"
        @input="page = $event"
        :key="`pagination-${reloadKey}`"
        :inert="inertProp || anyMenusOpen"
      />
    </template>
  </PaymentsTable>
</template>

<script>
import PaymentsTable from "./table.payments-dashboard";
import invoicesTableActions from "@/mixins/invoices/invoices-table-actions.mixin";
import TableDateFilterPanel from "@/components/tables/TableDateFilterPanel";
import TableOptionsFilterPanel from "@/components/tables/TableOptionsFilterPanel";
import TableActiveFilters from "@/components/tables/TableActiveFilters";
import paginatedTable from "@/mixins/paginated-table.mixin";
import TablePagination from "@/components/tables/TablePagination";
import TableOptionsResetBtn from "@/components/tables/TableOptionsResetBtn";
import { profileToApi } from "@/lib/domains";

export default {
  mixins: [invoicesTableActions, paginatedTable],
  components: {
    PaymentsTable,
    TableDateFilterPanel,
    TableOptionsFilterPanel,
    TableActiveFilters,
    TablePagination,
    TableOptionsResetBtn,
  },
  props: {
    title: String,
  },
  data() {
    return {
      list: [],
      loadingList: false,
      listOptions: this.getDefaultListOptions(false),
      filterFields: [
        { name: "user_id", label: "Creator" },
        { name: "internal_project_id", label: "Project" },
        { name: "created_at", label: "Date" },
        { name: "status", label: "Status" },
        { name: "skill_id", label: "Role" },
      ],
      reloadKey: 1,
      paginationEnabled: false,
      apiDomain: profileToApi()[window.location.origin],
      menus: {
        filters: false,
        download: false,
        table: false,
      },
      searchInputBuffer: "",
    };
  },
  mounted() {
    this.paginationEnabled = ["sm", "xs"].includes(this.$size);
    this.fetchList();
  },
  methods: {
    getParams() {
      const params = { ...this.listParams };
      if (["sm", "xs"].includes(this.$size)) {
        this.paginationEnabled = true;
        params.page = this.page;
        params.per = 5;
      } else {
        this.paginationEnabled = false;
      }
      return params;
    },
    async fetchList() {
      this.loadingList = true;
      const params = this.getParams();
      const { data } = await this.$api.Invoice.payments(params);
      this.list = data.invoices;
      if (this.paginationEnabled) {
        this.totalPages = data.total_pages;
      } else {
        this.totalPages = null;
      }
      this.loadingList = false;
      if (this.focusOnSearch) {
        this.focusOnSearch = false;
        this.$nextTick(() => this.$refs.searchField.$refs.input.focus());
      }
    },
    async refresh() {
      this.$emit("refresh");
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/styles/payments/payments-section.scss";
@import "@/styles/table-filters-menu.scss";
</style>
